//
import DynatraceClient from '@/plugins/dynatracePlugin';
import type { UnlockMode } from '@/plugins/auth/utils/definitions';
import {
  canUseBiometrics,
  getUnlockModePreference,
  hasStrongBiometricsStrengthLevel,
  setUnlockMode,
} from '@/plugins/auth/utils/sessionManager';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { useGlobalStore } from '@/stores/global/global.store';
import { RouteNames } from '@/router/routeNames';

export enum BootstrapTasks {
  BIOMETRICS_PROMPT = 'BiometricsPrompt',
  TERMS_OF_USE = 'TermsOfUse',
  POLICY_ACKNOWLEDGEMENT = 'PolicyAcknowledgement',
  DEEPLINK = 'Deeplink',
}

export async function getBootstrapTasks(): Promise<BootstrapTasks[]> {
  const tasks: BootstrapTasks[] = [];
  const globalStore = useGlobalStore();
  const isNativePlatform = CapacitorFacade.isNativePlatform();
  let _canUseBiometricsAndNoPreferenceYet = false;
  if (isNativePlatform) {
    // TODO: try Promise.allSettled
    const [_unlockModePreference, _canUseBiometrics, _hasStrongBiometricsStrengthLevel] =
      await Promise.all([
        getUnlockModePreference(),
        canUseBiometrics(),
        hasStrongBiometricsStrengthLevel(),
      ]);
    _canUseBiometricsAndNoPreferenceYet =
      !_unlockModePreference && _canUseBiometrics && _hasStrongBiometricsStrengthLevel;

    // If there was [device preference] already set, we don’t show the prompt/splash, but set config to the [device preference]
    if (!!_unlockModePreference && _canUseBiometrics && _hasStrongBiometricsStrengthLevel) {
      await setUnlockMode(_unlockModePreference as UnlockMode).catch(async (err) => {
        try {
          DynatraceClient.reportCustomError(
            'PC2_MOBILE_ERR',
            `[PostLoginLobby] ${err?.code}: ${err?.message || JSON.stringify(err)}`
          );
          await globalStore.postAuditLog({
            actionId: 'MobileApp_Device_FailureAction', // optional
            details: `[${Date.now()}] ERROR CODE ${err?.code}`, // Additional message stored - optional
            message: err?.message, // Error type - optional
          });
        } catch (_error) {
          /* empty */
        }
      });
    }
  }

  // If no preference set yet and can use biometrics, show the biometrics prompt/splash;
  const hasToShowBiometricsPrompt = isNativePlatform && _canUseBiometricsAndNoPreferenceYet;

  // call bootstrap to initiate post login tasks
  try {
    if (!globalStore.initialized) {
      await globalStore.bootstrap();
    }
  } catch (_e) {
    // TODO: log in DT
    // return { name: RouteNames.ERROR };
  }

  if (hasToShowBiometricsPrompt) tasks.push(BootstrapTasks.BIOMETRICS_PROMPT);
  if (globalStore.hasTermsOfUseTask) tasks.push(BootstrapTasks.TERMS_OF_USE);
  if (globalStore.hasPolicyAckTask) tasks.push(BootstrapTasks.POLICY_ACKNOWLEDGEMENT);
  if (window.localStorage.getItem('deeplinkroute')) tasks.push(BootstrapTasks.DEEPLINK);
  return tasks;
}

export async function getNextBootstrapRoute(tasks: BootstrapTasks[]) {
  const globalStore = useGlobalStore();
  globalStore.rootLoading = false;

  // handle routing based on post login tasks
  if (tasks.includes(BootstrapTasks.BIOMETRICS_PROMPT)) {
    return { name: RouteNames.BIOMETRICS_SPLASH };
  } else if (tasks.includes(BootstrapTasks.TERMS_OF_USE)) {
    return { name: RouteNames.TERMS_OF_USE, params: { contractId: '0' } };
  } else if (tasks.includes(BootstrapTasks.POLICY_ACKNOWLEDGEMENT)) {
    await globalStore.getDocuments();
    return {
      name: RouteNames.POLICY_ACKNOWLEDGEMENT,
      params: { contractId: '0' },
    };
  } else {
    // * consider deeplink routes (this might be a path, NOT name of a route)
    const deeplinkroute = window.localStorage.getItem('deeplinkroute');
    window.localStorage.removeItem('deeplinkroute'); // remove the deeplink route as usage is done
    const nextRoute = deeplinkroute ?? RouteNames.DASHBOARD; // there are no more post login tasks
    const nextRouteIsNamedRoute = Object.values<string>(RouteNames).includes(nextRoute);
    const _postLoginRoute = nextRouteIsNamedRoute ? { name: nextRoute } : { path: nextRoute };
    return _postLoginRoute;
  }
}
